import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  VALIDADE_DO_CODIGO_DE_SENHA_HORAS,
  type CodigoDeSenhaGerado,
  type MembroDaEquipe,
} from '@arena/shared/schemas';
import { perfisApi } from '../api/acessos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Nota, Quem } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';

/**
 * "Esqueci a senha", pela ponta do Dono.
 *
 * O produto não envia e-mail nem mensagem — então o caminho de quem
 * esqueceu a senha passa por aqui: o Dono gera um código de uso único e
 * entrega à pessoa pelo canal que os dois já usam. Ela entra com e-mail,
 * código e a senha nova na própria tela de login.
 *
 * O código aparece UMA vez. O servidor guarda só o hash dele, e não há
 * rota que o mostre de novo — perdido, é gerar outro, e gerar outro
 * invalida este. A tela diz isso ANTES de gerar, porque o Dono que fecha
 * o modal sem copiar não tem para onde voltar.
 */
export function ModalDeCodigoDeSenha({
  membro,
  onFechar,
}: {
  membro: MembroDaEquipe;
  onFechar: () => void;
}) {
  const avisar = useToast();
  const [gerado, setGerado] = useState<CodigoDeSenhaGerado | null>(null);
  const [falha, setFalha] = useState<string | null>(null);

  const gerar = useMutation({
    mutationFn: () => perfisApi.gerarCodigoDeSenha(membro.membershipId),
    onSuccess: setGerado,
    onError: (erro) => setFalha(mensagemDoErro(erro)),
  });

  async function copiar() {
    if (!gerado) return;
    try {
      await navigator.clipboard.writeText(gerado.codigo);
      avisar('Código copiado.');
    } catch {
      // Área de transferência bloqueada (http, permissão negada): o
      // código está na tela, e selecionar à mão funciona.
      avisar('Não consegui copiar — selecione o código na tela.');
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      marca={
        <span className="chip" style={{ background: 'var(--panel-3)' }} aria-hidden="true">
          <Icone nome="lock" />
        </span>
      }
      titulo={gerado ? 'Código gerado' : 'Gerar código de senha'}
      descricao={membro.nome}
      rodape={
        gerado ? (
          <>
            <Botao variante="contorno" onClick={() => void copiar()}>
              Copiar código
            </Botao>
            <Botao onClick={onFechar}>Entendi, já copiei</Botao>
          </>
        ) : (
          <>
            <Botao variante="fantasma" onClick={onFechar}>
              Cancelar
            </Botao>
            <Botao onClick={() => gerar.mutate()} disabled={gerar.isPending}>
              {gerar.isPending ? 'Gerando…' : 'Gerar código'}
            </Botao>
          </>
        )
      }
    >
      <CorpoDeModal>
        <Quem nome={membro.nome} detalhe={membro.email} />

        {gerado ? (
          <>
            <Campo
              rotulo="Código de uso único"
              ajuda={`Vale por ${VALIDADE_DO_CODIGO_DE_SENHA_HORAS} horas e serve uma vez só.`}
            >
              <input
                readOnly
                value={gerado.codigo}
                onFocus={(e) => e.currentTarget.select()}
                style={{
                  fontFamily: 'var(--mono, monospace)',
                  fontSize: 22,
                  letterSpacing: 3,
                  textAlign: 'center',
                }}
              />
            </Campo>
            <Nota>
              <b>Este código não aparece de novo.</b> O servidor guarda só o hash dele. Passe para{' '}
              {membro.nome.split(' ')[0]} agora — na tela de login, em "Esqueci a senha", ela entra
              com o e-mail, este código e a senha nova. Trocar a senha <b>encerra todas as sessões</b>{' '}
              dela.
            </Nota>
          </>
        ) : (
          <p className="sub">
            Gera um código de uso único para {membro.nome.split(' ')[0]} definir uma senha nova. Ele
            vale por {VALIDADE_DO_CODIGO_DE_SENHA_HORAS} horas, serve uma vez, e aparece aqui{' '}
            <b>uma única vez</b> — o servidor não guarda o código, só o hash. Gerar um novo invalida
            o anterior, e a geração fica na trilha com o seu nome.
          </p>
        )}

        {falha && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{falha}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
